/* TODO:
 * global read-only mode
 * use Material v3 compliant way for deactivated (read-only) components
 */

import IconButton from "@/components/ui/icon-button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge, type DocumentStatus } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { CURRENCIES } from "@/constants";
import { FileViewer } from "./FileViewer/FileViewer";
import { TagManager } from "@/features/tags";
import { useUsers } from "@/hooks/useUsers";
import { DecimalInput } from "@/components/ui/decimal-input";
import { DiscountInput } from "@/components/ui/discount-input";
import { DateInput } from "@/components/ui/custom-date-input";
import { TextInput } from "@/components/ui/text-input";
import { ValidatedSelect } from "@/components/ui/ValidatedSelect";
import { ValidationAlert } from "@/components/ui/ValidationAlert";
import { format as dateFnsFormat } from "date-fns";
import { ActionBar } from "./ActionBar";
import { LineItemCard } from "./LineItemCard";
import { useDocumentEditor, type DocumentEditorProps } from "./hooks/useDocumentEditor";
const formatDate = (
    dateValue: string | undefined | null,
    formatString: string,
    fallback: string = "Invalid date",
): string => {
    if (!dateValue) return fallback;
    try {
        return dateFnsFormat(new Date(dateValue), formatString);
    } catch (e) {
        console.error(`Invalid date format: ${dateValue}`, e);
        return fallback;
    }
};

export function DocumentEditor(props: DocumentEditorProps) {
    const { document, isLoading = false, hasNext = false, hasPrevious = false, onDelete } = props;
    const { getUserDisplayName } = useUsers();
    
    const {
        editedDocument,
        isReadOnly,
        isSaving,
        isApproving,
        isDeleting,
        showRevokeConfirmation,
        setShowRevokeConfirmation,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        showDiscardConfirmation,
        fieldErrors,
        hasErrors,
        hasChanges,
        canDelete,
        canApprove,
        canSave,
        handleInputChange,
        handleLineItemChange,
        addLineItem,
        deleteDocumentItem,
        handleUndo,
        handleSave,
        handleApprove,
        handleRevokeApprovalConfirm,
        handleRevokeApproval,
        handleDeleteConfirm,
        handleDelete,
        handleNavigate,
        confirmDiscardChanges,
        cancelDiscardChanges,
    } = useDocumentEditor(props);

    return (
        <div className="px-3 pt-4 pb-1 md:h-[calc(100vh-3rem)] md:flex md:flex-col">
            {/* Confirmation Dialog for Revoking Approval */}
            <ConfirmationDialog
                open={showRevokeConfirmation}
                onOpenChange={setShowRevokeConfirmation}
                title="Revoke Approval"
                description="This document will be excluded from accounting reports and data exports. Any retrospective changes may violate data consistency. You are responsible for maintaining data consistency across your accounting systems. Are you sure you want to revoke approval?"
                confirmText="Revoke Approval"
                onConfirm={handleRevokeApproval}
                isLoading={isApproving}
                variant="destructive"
            />

            {/* Confirmation Dialog for Deleting Receipt */}
            <ConfirmationDialog
                open={showDeleteConfirmation}
                onOpenChange={setShowDeleteConfirmation}
                title="Delete Receipt"
                description="Are you sure you want to delete this receipt? This action cannot be undone and all receipt data will be permanently removed."
                confirmText="Delete"
                onConfirm={handleDelete}
                isLoading={isDeleting}
                variant="destructive"
            />

            {/* Unified discard confirmation dialog */}
            <ConfirmationDialog
                open={showDiscardConfirmation}
                onOpenChange={cancelDiscardChanges}
                title="Discard Changes"
                description="You have unsaved changes. Are you sure you want to discard them?"
                confirmText="Discard"
                cancelText="Cancel"
                onConfirm={confirmDiscardChanges}
                variant="destructive"
            />

            {/* Use the ActionBar component */}
            <ActionBar
                isReadOnly={isReadOnly}
                isLoading={isLoading}
                isSaving={isSaving}
                isApproving={isApproving}
                isDeleting={isDeleting}
                onNavigate={handleNavigate}
                onSave={handleSave}
                onApprove={handleApprove}
                onRevokeApproval={handleRevokeApprovalConfirm}
                onDelete={onDelete ? handleDeleteConfirm : undefined}
                onUndo={handleUndo}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                hasChanges={hasChanges}
                canApprove={canApprove(document.status)}
                canDelete={canDelete(document.status)}
                canSave={canSave(document.status)}
            />

            <div className="flex flex-col md:flex-row gap-2 mt-2 md:flex-1 md:overflow-hidden">
                {/* Left column - Receipt image */}

                <Card className="w-full md:h-full md:w-1/2 p-0 flex justify-center items-center">
                    <FileViewer
                        isPdf={document.contentType === "application/pdf"}
                        fileKey={document.fileKey}
                    />
                </Card>

                {/* Right column - Receipt details */}

                <Card className="w-full md:h-full md:w-1/2">
                    <CardContent className="md:h-full md:overflow-y-auto">
                        <div className="mb-6 mt-0 flex flex-row flex-wrap-reverse justify-between items-end gap-4">
                            {/* Tag Management */}

                            <TagManager
                                fileTags={editedDocument.fileTags || ""}
                                onTagsChange={(newFileTags) =>
                                    handleInputChange("fileTags", newFileTags)
                                }
                                disabled={isReadOnly}
                            />
                            {/* Status Indicator */}
                            <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-2">
                                    {isReadOnly ? (
                                        <StatusBadge status="APPROVED" />
                                    ) : (
                                        <StatusBadge
                                            status={
                                                document.status as DocumentStatus
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Validation Alert */}
                        <ValidationAlert
                            errors={fieldErrors}
                            show={hasErrors && !isReadOnly}
                        />

                        {/* Invoice-like Header */}
                        <div className="mb-16">
                            <div className="flex flex-col xl:flex-row justify-between mb-3 gap-5">
                                {/* Left side - Vendor info */}
                                <div className="space-y-1 w-full">
                                    <div className="text-lg font-bold">
                                        <TextInput
                                            id="vendorName"
                                            value={editedDocument.vendorName}
                                            onChange={(value) =>
                                                handleInputChange(
                                                    "vendorName",
                                                    value,
                                                )
                                            }
                                            error={fieldErrors.vendorName}
                                            readOnly={isReadOnly}
                                            className={`h-9 text-lg font-bold ${isReadOnly ? "bg-md-surface-variant/50" : ""}`}
                                            placeholder="Vendor Name"
                                        />
                                    </div>
                                    <div>
                                        <Textarea
                                            id="vendorAddress"
                                            value={
                                                editedDocument.vendorAddress ||
                                                ""
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "vendorAddress",
                                                    e.target.value,
                                                )
                                            }
                                            readOnly={isReadOnly}
                                            className={`min-h-[114px] text-sm ${isReadOnly ? "bg-md-surface-variant/50" : ""}`}
                                            placeholder="Vendor Address"
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <Input
                                            id="vendorPhone"
                                            value={
                                                editedDocument.vendorPhone || ""
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "vendorPhone",
                                                    e.target.value,
                                                )
                                            }
                                            readOnly={isReadOnly}
                                            className={`h-9 text-sm ${isReadOnly ? "bg-md-surface-variant/50" : ""}`}
                                            placeholder="Vendor Phone"
                                        />
                                    </div>
                                </div>

                                {/* Right side - Receipt info */}
                                <div className="text-right space-y-1 min-w-70">
                                    {/* Document identification group */}

                                    <div className="flex items-start justify-end gap-2">
                                        <Label
                                            htmlFor="invoiceReceiptId"
                                            className="text-sm font-medium h-9 flex items-center"
                                        >
                                            Invoice #:
                                        </Label>
                                        <div className="w-40">
                                            <TextInput
                                                id="invoiceReceiptId"
                                                value={
                                                    editedDocument.invoiceReceiptId
                                                }
                                                onChange={(value) =>
                                                    handleInputChange(
                                                        "invoiceReceiptId",
                                                        value,
                                                    )
                                                }
                                                error={
                                                    fieldErrors.invoiceReceiptId
                                                }
                                                readOnly={isReadOnly}
                                                className="h-9"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="customerNumber"
                                            className="text-sm font-medium"
                                        >
                                            Customer #:
                                        </Label>
                                        <Input
                                            id="customerNumber"
                                            value={
                                                editedDocument.customerNumber ||
                                                ""
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "customerNumber",
                                                    e.target.value,
                                                )
                                            }
                                            readOnly={isReadOnly}
                                            className={`h-9 w-40 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        />
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="accountNumber"
                                            className="text-sm font-medium"
                                        >
                                            Account #:
                                        </Label>
                                        <Input
                                            id="accountNumber"
                                            value={
                                                editedDocument.accountNumber ||
                                                ""
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "accountNumber",
                                                    e.target.value,
                                                )
                                            }
                                            readOnly={isReadOnly}
                                            className={`h-9 w-40 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        />
                                    </div>
                                    <div className="flex items-start justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="poNumber"
                                            className="text-sm font-medium"
                                        >
                                            PO #:
                                        </Label>
                                        <Input
                                            id="poNumber"
                                            value={
                                                editedDocument.poNumber || ""
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "poNumber",
                                                    e.target.value,
                                                )
                                            }
                                            readOnly={isReadOnly}
                                            className={`h-9 w-40 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        />
                                    </div>

                                    <div className="pt-4 flex items-start justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="invoiceReceiptDate"
                                            className="text-sm font-medium h-9 flex items-center"
                                        >
                                            Invoice Date:
                                        </Label>
                                        <div className="w-40">
                                            <DateInput
                                                id="invoiceReceiptDate"
                                                value={
                                                    editedDocument.invoiceReceiptDate
                                                }
                                                onChange={(value) =>
                                                    handleInputChange(
                                                        "invoiceReceiptDate",
                                                        value,
                                                    )
                                                }
                                                error={
                                                    fieldErrors.invoiceReceiptDate
                                                }
                                                readOnly={isReadOnly}
                                                className="h-9"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="dueDate"
                                            className="text-sm font-medium"
                                        >
                                            Due Date:
                                        </Label>
                                        <div className="w-40">
                                            <DateInput
                                                id="dueDate"
                                                value={editedDocument.dueDate}
                                                onChange={(value) =>
                                                    handleInputChange(
                                                        "dueDate",
                                                        value,
                                                    )
                                                }
                                                readOnly={isReadOnly}
                                                className="h-9"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="orderDate"
                                            className="text-sm font-medium"
                                        >
                                            Order Date:
                                        </Label>
                                        <div className="w-40">
                                            <DateInput
                                                id="orderDate"
                                                value={editedDocument.orderDate}
                                                onChange={(value) =>
                                                    handleInputChange(
                                                        "orderDate",
                                                        value,
                                                    )
                                                }
                                                readOnly={isReadOnly}
                                                className="h-9 w-40"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-1">
                                        <Label
                                            htmlFor="deliveryDate"
                                            className="text-sm font-medium"
                                        >
                                            Delivery Date:
                                        </Label>
                                        <div className="w-40">
                                            <DateInput
                                                id="deliveryDate"
                                                value={
                                                    editedDocument.deliveryDate
                                                }
                                                onChange={(value) =>
                                                    handleInputChange(
                                                        "deliveryDate",
                                                        value,
                                                    )
                                                }
                                                readOnly={isReadOnly}
                                                className="h-9 w-40"
                                            />
                                        </div>
                                    </div>

                                    {/* Currency and pricing group - vertically separated */}

                                    <div className="pt-4">
                                        <div className="flex flex-col items-end gap-1 ">
                                            <div className="flex items-start justify-end gap-2">
                                                <Label
                                                    htmlFor="currency"
                                                    className="text-sm font-medium h-9 flex items-center"
                                                >
                                                    Currency:
                                                </Label>
                                                {/* Do not default to any value */}
                                                <ValidatedSelect
                                                    field="currency"
                                                    label=""
                                                    value={
                                                        editedDocument.currency
                                                    }
                                                    onChange={(value) =>
                                                        handleInputChange(
                                                            "currency",
                                                            value,
                                                        )
                                                    }
                                                    error={fieldErrors.currency}
                                                    readOnly={isReadOnly}
                                                    options={CURRENCIES.map(
                                                        (c) => ({
                                                            value: c.code,
                                                            label: c.code,
                                                            description: c.name,
                                                        }),
                                                    )}
                                                    placeholder="Select Currency"
                                                    className="h-9 w-40"
                                                />
                                            </div>
                                        </div>
                                        {/* TODO: implement as tax part of tax update */}
                                        {/* <div className="flex items-center justify-end gap-2 mt-1">
                                            <Label
                                                htmlFor="isGrossPricing"
                                                className="text-sm font-medium"
                                            >
                                                Pricing Type:
                                            </Label>
                                            <Select
                                                value={
                                                    taxInfo.isGrossPricing
                                                        ? "gross"
                                                        : "net"
                                                }
                                                onValueChange={(value) => {
                                                    handleTaxInfoChange({
                                                        ...taxInfo,
                                                        isGrossPricing:
                                                            value === "gross",
                                                    });
                                                }}
                                                disabled={
                                                    isReadOnly
                                                }
                                            >
                                                <SelectTrigger className="h-9 w-40">
                                                    <SelectValue placeholder="Select pricing type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="gross">
                                                        Gross (incl. tax)
                                                    </SelectItem>
                                                    <SelectItem value="net">
                                                        Net (excl. tax)
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div> */}

                                        {/* Tax Rate Configuration */}
                                        {/* TODO: implement as tax part of tax update */}
                                        {/* <div className="flex items-center justify-end gap-2 mt-1 h-9">
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    id="useSingularTaxRate"
                                                    checked={isSingleTaxRate}
                                                    onCheckedChange={(
                                                        checked
                                                    ) => {
                                                        if (isReadOnly)
                                                            return;
                                                        setIsSingleTaxRate(
                                                            checked === true
                                                        );
                                                        handleInputChange(
                                                            "useSingularTaxRate",
                                                            checked === true
                                                        );
                                                        handleTaxInfoChange({
                                                            ...taxInfo,
                                                            useSingularTaxRate:
                                                                checked ===
                                                                true,
                                                        });
                                                    }}
                                                    disabled={
                                                        isReadOnly ||
                                                        false
                                                    }
                                                />
                                                <Label
                                                    htmlFor="useSingularTaxRate"
                                                    className="text-sm font-medium"
                                                >
                                                    Use single tax rate
                                                </Label>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <Separator className="mb-6" /> */}

                        {/* Receipt Items */}
                        <div className="mb-6">
                            {/* Items as cards */}
                            <div className="space-y-3">
                                {!Array.isArray(editedDocument.lineItems) ||
                                editedDocument.lineItems.length === 0 ? (
                                    <div className="text-center text-sm text-gray-500 py-4">
                                        No items found
                                    </div>
                                ) : (
                                    editedDocument.lineItems.map(
                                        (item, index) => (
                                            <LineItemCard
                                                key={`item-${index}`}
                                                item={item}
                                                index={index}
                                                isReadOnly={isReadOnly}
                                                useMixedTaxRates={false}
                                                onLineItemChange={
                                                    handleLineItemChange
                                                }
                                                onDelete={deleteDocumentItem}
                                            />
                                        ),
                                    )
                                )}

                                {/* Add Line Item Button */}
                                {!isReadOnly && (
                                    <div className="flex flex-row justify-center mt-1">
                                        <IconButton
                                            onClick={addLineItem}
                                            size="sm"
                                            color="primary"
                                            variant="outlined"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </IconButton>
                                    </div>
                                )}

                                {/* Items total */}
                                {Array.isArray(editedDocument.lineItems) &&
                                    editedDocument.lineItems.length > 0 && (
                                        <div className="bg-md-surface-variant/50 px-6 py-3 rounded-md flex justify-between font-semibold">
                                            <span>Items Total:</span>
                                            <span>
                                                {editedDocument.lineItems
                                                    .reduce(
                                                        (sum, item) =>
                                                            sum +
                                                            (item.price
                                                                ? item.price
                                                                : (item.unitPrice ||
                                                                      0) *
                                                                  (item.quantity ||
                                                                      1)),
                                                        0,
                                                    )
                                                    .toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        </div>

                        {/* Financial Information Section */}
                        {/* <Separator /> */}
                        <div className="my-10">
                            <h3 className=" font-medium">
                                Payment Details & Adjustments
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {/* Service Charge */}
                                <div>
                                    <Label
                                        htmlFor="serviceCharge"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Service Charge
                                    </Label>
                                    <DecimalInput
                                        id="serviceCharge"
                                        value={editedDocument.serviceCharge}
                                        onChange={(value) =>
                                            handleInputChange(
                                                "serviceCharge",
                                                value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        error={fieldErrors.serviceCharge}
                                    />
                                    <div className="text-xs text-gray-500 mt-1">
                                        Mandatory fee
                                    </div>
                                </div>

                                {/* Discount */}
                                <div>
                                    <Label
                                        htmlFor="discount"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Discount
                                    </Label>
                                    <DiscountInput
                                        id="discount"
                                        value={editedDocument.discount}
                                        onChange={(value) =>
                                            handleInputChange("discount", value)
                                        }
                                        readOnly={isReadOnly}
                                        error={fieldErrors.discount}
                                        placeholder="0"
                                    />
                                </div>

                                {/* Payment Method */}
                                <div>
                                    <Label
                                        htmlFor="paymentTerms"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Payment Terms
                                    </Label>
                                    <Input
                                        id="paymentTerms"
                                        value={
                                            editedDocument.paymentTerms || ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "paymentTerms",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Cash, Credit Card, etc."
                                    />
                                </div>

                                {/* Gratuity */}
                                <div>
                                    <Label
                                        htmlFor="gratuity"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Gratuity/Tip
                                    </Label>
                                    <DecimalInput
                                        id="gratuity"
                                        value={editedDocument.gratuity}
                                        onChange={(value) =>
                                            handleInputChange("gratuity", value)
                                        }
                                        readOnly={isReadOnly}
                                        error={fieldErrors.gratuity}
                                    />
                                    <div className="text-xs text-gray-500 mt-1">
                                        Voluntary amount given by customer
                                    </div>
                                </div>

                                {/* Shipping & Handling */}
                                <div>
                                    <Label
                                        htmlFor="shippingHandlingCharge"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Shipping & Handling
                                    </Label>
                                    <DecimalInput
                                        id="shippingHandlingCharge"
                                        value={
                                            editedDocument.shippingHandlingCharge
                                        }
                                        onChange={(value) =>
                                            handleInputChange(
                                                "shippingHandlingCharge",
                                                value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        error={
                                            fieldErrors.shippingHandlingCharge
                                        }
                                    />
                                </div>

                                {/* Prior Balance */}
                                <div>
                                    <Label
                                        htmlFor="priorBalance"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Prior Balance
                                    </Label>
                                    <DecimalInput
                                        id="priorBalance"
                                        value={editedDocument.priorBalance}
                                        onChange={(value) =>
                                            handleInputChange(
                                                "priorBalance",
                                                value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        error={fieldErrors.priorBalance}
                                    />
                                </div>

                                {/* Amount Due */}
                                <div>
                                    <Label
                                        htmlFor="amountDue"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Amount Due
                                    </Label>
                                    <DecimalInput
                                        id="amountDue"
                                        value={editedDocument.amountDue}
                                        onChange={(value) =>
                                            handleInputChange(
                                                "amountDue",
                                                value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        error={fieldErrors.amountDue}
                                    />
                                </div>

                                {/* Amount Paid */}
                                <div>
                                    <Label
                                        htmlFor="amountPaid"
                                        className="mb-1.5 block font-medium"
                                    >
                                        Amount Paid
                                    </Label>
                                    <DecimalInput
                                        id="amountPaid"
                                        value={editedDocument.amountPaid}
                                        onChange={(value) =>
                                            handleInputChange(
                                                "amountPaid",
                                                value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        error={fieldErrors.amountPaid}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Totals Section */}
                        {/* <Separator /> */}
                        <div className="my-10">
                            <h3 className="text-xl font-semibold mb-4">
                                Receipt Total
                                {/* TODO: <p className="text-sm font-normal text-gray-500">
                                            {taxInfo.isGrossPricing
                                                ? "(Gross - incl. tax)"
                                                : "(Net - excl. tax)"}
                                        </p> */}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {/* Calculation Breakdown */}
                                <div className="">
                                    {/* <h4 className="text-sm font-medium mb-1 text-gray-700">
                                        Calculation Breakdown
                                    </h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Verify if extracted values are correct
                                        &mdash; compare calculated total with
                                        receipt total
                                    </p>
                                    <CalculationTable
                                        document={editedDocument}
                                    /> */}
                                </div>
                                <div className="flex justify-center">
                                    <Card className="p-4 bg-md-surface-level-2 max-w-58">
                                        <div className="space-y-3">
                                            <div>
                                                <Label
                                                    htmlFor="total"
                                                    className="mb-1.5 block font-medium"
                                                >
                                                    Total Amount
                                                </Label>
                                                <DecimalInput
                                                    id="total"
                                                    value={editedDocument.total}
                                                    onChange={(value) =>
                                                        handleInputChange(
                                                            "total",
                                                            value,
                                                        )
                                                    }
                                                    error={fieldErrors.total}
                                                    readOnly={isReadOnly}
                                                    className="text-right"
                                                />
                                            </div>

                                            <div>
                                                <Label
                                                    htmlFor="subtotal"
                                                    className="mb-1.5 block font-medium"
                                                >
                                                    Subtotal Amount
                                                </Label>
                                                <DecimalInput
                                                    id="subtotal"
                                                    value={
                                                        editedDocument.subtotal
                                                    }
                                                    onChange={(value) =>
                                                        handleInputChange(
                                                            "subtotal",
                                                            value,
                                                        )
                                                    }
                                                    readOnly={isReadOnly}
                                                    error={fieldErrors.subtotal}
                                                    className="text-right"
                                                />
                                            </div>

                                            <>
                                                <div>
                                                    <Label
                                                        htmlFor="tax"
                                                        className="mb-1.5 block font-medium"
                                                    >
                                                        Tax Amount
                                                    </Label>
                                                    <DecimalInput
                                                        id="tax"
                                                        value={
                                                            editedDocument.tax
                                                        }
                                                        onChange={(value) =>
                                                            handleInputChange(
                                                                "tax",
                                                                value,
                                                            )
                                                        }
                                                        error={fieldErrors.tax}
                                                        readOnly={isReadOnly}
                                                        className="text-right"
                                                    />
                                                </div>
                                            </>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </div>

                        {/* Credit Card Information */}
                        {/* <Separator /> */}
                        <div className="my-10">
                            <h3 className="font-medium">
                                Credit Card Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <Label
                                        htmlFor="creditCardType"
                                        className="mb-1 block text-sm"
                                    >
                                        Type
                                    </Label>
                                    <Input
                                        id="creditCardType"
                                        value={
                                            editedDocument.creditCardType || ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "creditCardType",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Credit Card Type"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="creditCardLastFour"
                                        className="mb-1 block text-sm"
                                    >
                                        Last Four Digits
                                    </Label>
                                    <Input
                                        id="creditCardLastFour "
                                        value={
                                            editedDocument.creditCardLastFour ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "creditCardLastFour",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Last Four Digits"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tax Information Section */}
                        {/* <Separator /> */}
                        <div className="my-10">
                            <h3 className="font-medium">
                                Vendor Tax Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <Label
                                        htmlFor="vendorGstNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        GST/HST Number{" "}
                                        <span className="text-xs text-gray-500">
                                            (Canada)
                                        </span>
                                    </Label>
                                    <Input
                                        id="vendorGstNumber"
                                        value={
                                            editedDocument.vendorGstNumber || ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "vendorGstNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="GST/HST Registration Number"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="vendorVatNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        VAT Number{" "}
                                        <span className="text-xs text-gray-500">
                                            (Europe)
                                        </span>
                                    </Label>
                                    <Input
                                        id="vendorVatNumber"
                                        value={
                                            editedDocument.vendorVatNumber || ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "vendorVatNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="VAT Registration Number"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="vendorAbnNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        ABN{" "}
                                        <span className="text-xs text-gray-500">
                                            (Australia)
                                        </span>
                                    </Label>
                                    <Input
                                        id="vendorAbnNumber"
                                        value={
                                            editedDocument.vendorAbnNumber || ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "vendorAbnNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Australian Business Number"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="vendorPanNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        PAN{" "}
                                        <span className="text-xs text-gray-500">
                                            (India)
                                        </span>
                                    </Label>
                                    <Input
                                        id="vendorPanNumber"
                                        value={
                                            editedDocument.vendorPanNumber || ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "vendorPanNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Permanent Account Number"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="taxPayerId"
                                        className="mb-1 block text-sm"
                                    >
                                        Tax ID{" "}
                                        <span className="text-xs text-gray-500">
                                            (General)
                                        </span>
                                    </Label>
                                    <Input
                                        id="taxPayerId"
                                        value={editedDocument.taxPayerId || ""}
                                        onChange={(e) =>
                                            handleInputChange(
                                                "taxPayerId",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Tax Identification Number"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Customer Tax Information */}
                        {/* <Separator /> */}
                        <div className="my-10">
                            <h3 className="font-medium">
                                Customer Tax Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <Label
                                        htmlFor="receiverGstNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        GST/HST Number{" "}
                                        <span className="text-xs text-gray-500">
                                            (Canada)
                                        </span>
                                    </Label>
                                    <Input
                                        id="receiverGstNumber"
                                        value={
                                            editedDocument.receiverGstNumber ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "receiverGstNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Customer GST/HST Number"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="receiverVatNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        VAT Number{" "}
                                        <span className="text-xs text-gray-500">
                                            (Europe)
                                        </span>
                                    </Label>
                                    <Input
                                        id="receiverVatNumber"
                                        value={
                                            editedDocument.receiverVatNumber ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "receiverVatNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Customer VAT Number"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="receiverAbnNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        ABN{" "}
                                        <span className="text-xs text-gray-500">
                                            (Australia)
                                        </span>
                                    </Label>
                                    <Input
                                        id="receiverAbnNumber"
                                        value={
                                            editedDocument.receiverAbnNumber ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "receiverAbnNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Customer ABN"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="receiverPanNumber"
                                        className="mb-1 block text-sm"
                                    >
                                        PAN{" "}
                                        <span className="text-xs text-gray-500">
                                            (India)
                                        </span>
                                    </Label>
                                    <Input
                                        id="receiverPanNumber"
                                        value={
                                            editedDocument.receiverPanNumber ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                "receiverPanNumber",
                                                e.target.value,
                                            )
                                        }
                                        readOnly={isReadOnly}
                                        className={`h-9 ${isReadOnly ? "bg-gray-50" : ""}`}
                                        placeholder="Customer PAN"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Audit Information */}
                        <div className="my-10">
                            <h3 className="font-medium">Audit Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <Label className="mb-1 block text-sm">
                                        Created At
                                    </Label>
                                    <Input
                                        value={
                                            formatDate(
                                                document.createdAt,
                                                "yyyy-MM-dd HH:mm:ss",
                                            ) || ""
                                        }
                                        readOnly
                                        className="h-9 bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-sm">
                                        Created By
                                    </Label>
                                    <Input
                                        value={getUserDisplayName(
                                            document.createdBy || "",
                                        )}
                                        readOnly
                                        className="h-9 bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-sm">
                                        Modified At
                                    </Label>
                                    <Input
                                        value={
                                            formatDate(
                                                document.modifiedAt,
                                                "yyyy-MM-dd HH:mm:ss",
                                            ) || ""
                                        }
                                        readOnly
                                        className="h-9 bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-sm">
                                        Modified By
                                    </Label>
                                    <Input
                                        value={getUserDisplayName(
                                            document.modifiedBy || "",
                                        )}
                                        readOnly
                                        className="h-9 bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-sm">
                                        Document ID
                                    </Label>
                                    <Input
                                        value={document.documentId || ""}
                                        readOnly
                                        className="h-9 bg-gray-50"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
